import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useApi } from '../lib/api'
import type { Order, Client, Flag } from '../types'
import { FLAG_MATERIALS } from '../types'
import { FlagThumbnail } from '../components/FlagThumbnail'
import { FlagPicker } from '../components/FlagPicker'

// ---------------------------------------------------------------------------
// Client Combobox
// ---------------------------------------------------------------------------
interface ClientComboboxProps {
  value: Client | null
  onChange: (c: Client | null) => void
}

function ClientCombobox({ value, onChange }: ClientComboboxProps) {
  const { apiFetch } = useApi()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [results, setResults] = useState<Client[]>([])
  const [loadingSearch, setLoadingSearch] = useState(false)
  const [showNewForm, setShowNewForm] = useState(false)
  const [newClient, setNewClient] = useState({ name: '', email: '', phone: '', company: '' })
  const [savingNew, setSavingNew] = useState(false)
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const search = useCallback((q: string) => {
    setLoadingSearch(true)
    const params = new URLSearchParams({ limit: '10' })
    if (q) params.set('q', q)
    apiFetch<{ clients: Client[] }>(`/api/clients?${params}`)
      .then(({ clients }) => setResults(clients))
      .finally(() => setLoadingSearch(false))
  }, [apiFetch]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleInputChange(val: string) {
    setQuery(val)
    setOpen(true)
    if (debounce.current) clearTimeout(debounce.current)
    debounce.current = setTimeout(() => search(val), 250)
  }

  function handleFocus() {
    setOpen(true)
    if (results.length === 0) search(query)
  }

  function handleSelect(c: Client) {
    onChange(c)
    setQuery('')
    setOpen(false)
    setShowNewForm(false)
  }

  function handleClear() {
    onChange(null)
    setQuery('')
    setResults([])
  }

  async function handleCreateNew() {
    if (!newClient.name) return
    setSavingNew(true)
    try {
      const created = await apiFetch<Client>('/api/clients', {
        method: 'POST',
        body: JSON.stringify(newClient),
      })
      handleSelect(created)
      setNewClient({ name: '', email: '', phone: '', company: '' })
    } finally {
      setSavingNew(false)
    }
  }

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setShowNewForm(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  if (value) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 12px', border: '1px solid var(--color-border, #cbd5e1)',
        borderRadius: 6, background: 'var(--color-bg-alt, #f1f5f9)',
      }}>
        <div style={{ flex: 1 }}>
          <span style={{ fontWeight: 600 }}>{value.name}</span>
          {value.company && <span style={{ color: 'var(--color-muted)', marginLeft: 6 }}>— {value.company}</span>}
          {value.email && <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>{value.email}</div>}
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          style={{ padding: '3px 10px', fontSize: 12 }}
          onClick={handleClear}
        >
          Ändern
        </button>
      </div>
    )
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        type="search"
        placeholder="Nach Name, E-Mail, Firma, Telefon suchen…"
        value={query}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={handleFocus}
        autoComplete="off"
        style={{ width: '100%' }}
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: '#fff', border: '1px solid var(--color-border, #cbd5e1)',
          borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          maxHeight: 320, overflowY: 'auto',
        }}>
          {loadingSearch && (
            <div style={{ padding: '10px 14px', color: 'var(--color-muted)', fontSize: 13 }}>Suche läuft…</div>
          )}
          {!loadingSearch && results.length === 0 && (
            <div style={{ padding: '10px 14px', color: 'var(--color-muted)', fontSize: 13 }}>Keine Kunden gefunden</div>
          )}
          {results.map((c) => (
            <div
              key={c.id}
              onMouseDown={() => handleSelect(c)}
              style={{
                padding: '9px 14px', cursor: 'pointer',
                borderBottom: '1px solid var(--color-border-light, #e2e8f0)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '')}
            >
              <span style={{ fontWeight: 500 }}>{c.name}</span>
              {c.company && <span style={{ color: 'var(--color-muted)', marginLeft: 6, fontSize: 13 }}>— {c.company}</span>}
              {c.email && <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>{c.email}</div>}
              {c.phone && <div style={{ fontSize: 12, color: 'var(--color-muted)' }}>{c.phone}</div>}
            </div>
          ))}

          {/* Create new */}
          {!showNewForm ? (
            <div
              onMouseDown={() => setShowNewForm(true)}
              style={{
                padding: '9px 14px', cursor: 'pointer', fontWeight: 500,
                color: 'var(--color-primary, #2563eb)', fontSize: 13,
                borderTop: results.length ? '1px solid var(--color-border, #cbd5e1)' : undefined,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#eff6ff')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '')}
            >
              + Neuen Kunden anlegen{query ? ` „${query}"` : ''}
            </div>
          ) : (
            <div
              onMouseDown={(e) => e.stopPropagation()}
              style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid var(--color-border, #cbd5e1)' }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13 }}>Neuer Kunde</div>
              <input
                required
                placeholder="Name *"
                value={newClient.name || query}
                onChange={(e) => setNewClient((f) => ({ ...f, name: e.target.value }))}
                onFocus={(e) => { if (!newClient.name && query) setNewClient((f) => ({ ...f, name: query })); e.target.select() }}
              />
              <input
                placeholder="Firma"
                value={newClient.company}
                onChange={(e) => setNewClient((f) => ({ ...f, company: e.target.value }))}
              />
              <input
                type="email"
                placeholder="E-Mail"
                value={newClient.email}
                onChange={(e) => setNewClient((f) => ({ ...f, email: e.target.value }))}
              />
              <input
                placeholder="Telefon"
                value={newClient.phone}
                onChange={(e) => setNewClient((f) => ({ ...f, phone: e.target.value }))}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <button type="button" className="btn btn-primary" style={{ fontSize: 12, padding: '4px 12px' }} disabled={savingNew || !newClient.name} onClick={handleCreateNew}>
                  {savingNew ? 'Wird erstellt…' : 'Anlegen & auswählen'}
                </button>
                <button type="button" className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => setShowNewForm(false)}>
                  Zurück
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

interface LocalItem {
  quantity: number
  width_cm: number
  height_cm: number
  material: string
  flag: Flag
}

const EMPTY_FORM = { quantity: 1, width_cm: 100, height_cm: 150, material: 'Polyester' }

export default function NewOrderPage() {
  const { apiFetch } = useApi()
  const navigate = useNavigate()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [client, setClient] = useState<Client | null>(null)
  const [localItems, setLocalItems] = useState<LocalItem[]>([])
  const [itemForm, setItemForm] = useState({ ...EMPTY_FORM })
  const [itemFlag, setItemFlag] = useState<Flag | null>(null)

  function addItem() {
    if (!itemForm.width_cm || !itemForm.height_cm || !itemForm.quantity || !itemFlag) return
    setLocalItems((prev) => [...prev, { ...itemForm, flag: itemFlag }])
    setItemForm({ ...EMPTY_FORM })
    setItemFlag(null)
  }

  function removeItem(index: number) {
    setLocalItems((prev) => prev.filter((_, i) => i !== index))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!client) { setError('Bitte wählen Sie einen Kunden aus'); return }
    if (localItems.length === 0) { setError('Bitte fügen Sie mindestens eine Position hinzu'); return }
    setError(null)
    setSaving(true)
    try {
      const order = await apiFetch<Order>('/api/orders', {
        method: 'POST',
        body: JSON.stringify({
          client_id: client.id,
          client_name: client.name,
          client_email: client.email ?? '',
        }),
      })
      for (const item of localItems) {
        await apiFetch(`/api/orders/${order.id}/items`, {
          method: 'POST',
          body: JSON.stringify({
            flag_id: item.flag.id,
            quantity: item.quantity,
            width_cm: item.width_cm,
            height_cm: item.height_cm,
            material: item.material,
          }),
        })
      }
      navigate(`/orders/${order.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Erstellen des Auftrags')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ maxWidth: 800 }}>
      <div className="page-header">
        <div>
          <Link to="/orders" style={{ color: 'var(--color-muted)', fontSize: 13 }}>← Aufträge</Link>
          <h1 style={{ marginTop: 4 }}>Neuer Auftrag</h1>
        </div>
      </div>

      <div className="card">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Kunde *</label>
            <ClientCombobox value={client} onChange={setClient} />
          </div>

          {/* Positions */}
          <div className="form-group" style={{ marginBottom: 20 }}>
            <label style={{ marginBottom: 10, display: 'block' }}>Positionen *</label>

            {localItems.length > 0 && (
              <table style={{ marginBottom: 12 }}>
                <thead>
                  <tr>
                    <th>Flagge</th>
                    <th>Anzahl</th>
                    <th>Größe (cm)</th>
                    <th>Material</th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {localItems.map((item, i) => (
                    <tr key={i}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <FlagThumbnail id={item.flag.id} type={item.flag.type} imageUrl={item.flag.image_url} name={item.flag.name} width={36} height={22} />
                          <span style={{ fontSize: 13 }}>{item.flag.name}</span>
                        </div>
                      </td>
                      <td>{item.quantity}×</td>
                      <td>{item.width_cm} × {item.height_cm}</td>
                      <td>{item.material}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          style={{ padding: '2px 8px', fontSize: 12, color: 'var(--color-danger, #ef4444)' }}
                          onClick={() => removeItem(i)}
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Add item row */}
            <div style={{ border: '1px solid var(--color-border, #cbd5e1)', borderRadius: 6, padding: 12 }}>
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 4 }}>Flagge *</div>
                <FlagPicker value={itemFlag} onChange={setItemFlag} />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 4 }}>Anzahl</div>
                  <input
                    type="number"
                    min={1}
                    value={itemForm.quantity}
                    onChange={(e) => setItemForm((f) => ({ ...f, quantity: parseInt(e.target.value) || 1 }))}
                    style={{ width: 70 }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 4 }}>Breite (cm)</div>
                  <input
                    type="number"
                    min={1}
                    value={itemForm.width_cm}
                    onChange={(e) => setItemForm((f) => ({ ...f, width_cm: parseInt(e.target.value) || 0 }))}
                    style={{ width: 90 }}
                  />
                </div>
                <div style={{ alignSelf: 'flex-end', paddingBottom: 8, color: 'var(--color-muted)' }}>×</div>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 4 }}>Höhe (cm)</div>
                  <input
                    type="number"
                    min={1}
                    value={itemForm.height_cm}
                    onChange={(e) => setItemForm((f) => ({ ...f, height_cm: parseInt(e.target.value) || 0 }))}
                    style={{ width: 90 }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 4 }}>Material</div>
                  <select
                    value={itemForm.material}
                    onChange={(e) => setItemForm((f) => ({ ...f, material: e.target.value }))}
                    style={{ width: 140 }}
                  >
                    {FLAG_MATERIALS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={addItem}
                  disabled={!itemForm.width_cm || !itemForm.height_cm || !itemFlag}
                >
                  + Hinzufügen
                </button>
              </div>
            </div>
          </div>

          {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Wird erstellt…' : 'Auftrag erstellen'}
            </button>
            <Link to="/orders" className="btn btn-secondary">Abbrechen</Link>
          </div>
        </form>
      </div>
    </div>
  )
}
